'use client';

import type { FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { JOURNEY_EDIT_ERROR, JourneyTransformed } from '~/types/Journey.types';
import { isDev } from '~/utils';
import { preserveHeaderImages } from './preserveHeaderImages';

type EditFormRef = FormikProps<{
  adult_count: any;
  child_count: any;
  childAges: any;
  room_count: any;
  closest_city: any;
  duration: number;
  startDate: Date | null;
  endDate: Date | null;
  twinRoomPreferred: any;
}>;

type JourneyActions = {
  setJourney: (journey: JourneyTransformed | null) => void;
  setCurrentJourneyTotal: (price: number | null) => void;
  setJourneyEditErrors: (errors: JOURNEY_EDIT_ERROR[]) => void;
  setFormRef: (ref: EditFormRef) => void;
  // ui
  setIsMobileEditPanelOpen: (isOpen: boolean) => void;
  setMobileEditPanelIndex: (index: number) => void;
  setIsCheckingAvailability: (isCheckingAvailability: boolean) => void;
};

type JourneyState = {
  journey: JourneyTransformed | null;
  currentJourneyTotal: number | null;
  actions: JourneyActions;
  journeyEditErrors: JOURNEY_EDIT_ERROR[];
  formRef: EditFormRef | null;
  // ui
  isMobileEditPanelOpen: boolean;
  mobileEditPanelIndex: number;
  isCheckingAvailability: boolean;
};

export const useJourneyStore = create<JourneyState>()(
  devtools(
    (set, _get) => ({
      journey: null,
      journeyEditErrors: [],
      currentJourneyTotal: null,
      // ui
      isMobileEditPanelOpen: false,
      mobileEditPanelIndex: 0,
      formRef: null,
      isCheckingAvailability: false,
      actions: {
        setJourneyEditErrors: (journeyEditErrors) => set(() => ({ journeyEditErrors })),
        setJourney: (journey) =>
          set((state) => ({
            journey: journey ? preserveHeaderImages(journey, state.journey) : journey,
            currentJourneyTotal: null
          })),
        setCurrentJourneyTotal: (total) => set(() => ({ currentJourneyTotal: total })),

        // ui
        setIsMobileEditPanelOpen: (isOpen) => set(() => ({ isMobileEditPanelOpen: isOpen })),
        setMobileEditPanelIndex: (index) => set(() => ({ mobileEditPanelIndex: index })),
        setFormRef: (formRef) => set(() => ({ formRef })),
        setIsCheckingAvailability: (isCheckingAvailability) => set(() => ({ isCheckingAvailability }))
      }
    }),
    { enabled: isDev(), name: 'journeyStore' }
  )
);

export function JourneyStoreInitializer({
  journey,
  children
}: {
  journey: JourneyTransformed | null;
  children: React.ReactNode;
}) {
  const setJourney = useJourneyStore((state) => state.actions.setJourney);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setJourney(journey);
    setReady(true);
  }, [journey, setJourney]);

  if (!ready) return null;

  return <>{children}</>;
}

const useJourney = () => useJourneyStore((state) => state.journey);
const useCurrentJourney = (): JourneyTransformed => {
  const journey = useJourneyStore((state) => state.journey);
  if (!journey) {
    throw new Error('Journey not found in store');
  }
  return journey;
};

const useCurrentJourneyTotal = () => useJourneyStore((state) => state.currentJourneyTotal);
const useJourneyActions = () => useJourneyStore((state) => state.actions);
const useJourneyEditErrors = () => useJourneyStore((state) => state.journeyEditErrors);

const useMobileEditPanel = () =>
  useJourneyStore((state) => {
    return {
      isMobileEditPanelOpen: state.isMobileEditPanelOpen,
      setIsMobileEditPanelOpen: state.actions.setIsMobileEditPanelOpen,
      mobileEditPanelIndex: state.mobileEditPanelIndex,
      setMobileEditPanelIndex: state.actions.setMobileEditPanelIndex
    };
  });
const useFormRef = () => useJourneyStore((state) => state.formRef);
const useIsCheckingAvailability = () => useJourneyStore((state) => state.isCheckingAvailability);

export {
  useCurrentJourney,
  useCurrentJourneyTotal,
  useFormRef,
  useIsCheckingAvailability,
  useJourney,
  useJourneyActions,
  useJourneyEditErrors,
  useMobileEditPanel
};
